import React, { FC, useEffect, useState, useRef } from 'react';
import locales from '../../../content.json';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import EveryDoggyAPIServices from '../../../utils/services/EveryDoggyAPIServices';
import errorLogger from '../../../utils/errorLogger/appErrorLogger';
import tryPaid from './helpers/tryPaid';
import { options, paymentRequestOptions } from './constants';
import { PAYMENT_SYSTEM } from './types';
import ComposeAppleGoogleViewModel from './viewModel';
import { IPaymentModalProduct } from '../../../reduxStore/data/state';
import { updatePurchaseFlow } from '../../../reduxStore/navigation/actions';
import { ToastContainer } from 'react-toastify';
import { PaymentActionResult } from '../paymentModal/checkout/types';
import s from './index.module.scss';
import Delimiter from '../paymentModal/checkout/components/delimiter';
import { RootState } from '../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { GTMQuizFunnelEvent, analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '../../../utils/analytics';
import { getOnboardingAnswersFromURL, getParameterFromURL } from '../../../utils/scripts';
import applePay from './img/aplePay.png';
import gpl from './img/gpl.png';
import getCurrencyValue from '../../../utils/scripts/getCurrencyValue';
import { getLocalizedError } from '../../../utils/scripts/getLocalizedError';

type Props = {
  product?: IPaymentModalProduct;
};

let productName;
let productAmount;
let productPlanId;
let paymentSystemValue;
let internalId;

const AppleGooglePayment: FC<Props> = props => {
  const { product } = props;
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentSystem, setPaymentSystem] = useState<PAYMENT_SYSTEM>(PAYMENT_SYSTEM.BROWSER_SAVED_CARD);
  const [appleError, setAppleError] = useState('');
  const viewModel = ComposeAppleGoogleViewModel(window.location.href, EveryDoggyAPIServices, errorLogger);
  const noPaypal = useSelector((state: RootState) => state.data.noPaypal);
  const productAmountActive = useSelector((state: RootState) => {
    return state.data.paymentModal.product.details ? state.data.paymentModal.product.details.amount : '';
  });
  const productTrialAmountActive = useSelector((state: RootState) => {
    return state.data.paymentModal.product.details ? state.data.paymentModal.product.details.trial_amount : '';
  });
  const amountValue = Number(productTrialAmountActive) || productAmountActive;
  const productNameActive = useSelector((state: RootState) => {
    return state.data.paymentModal.product ? state.data.paymentModal.productName?.name : '';
  });
  const showChatUpsale = useSelector((state: RootState) => state.navigation.purchaseFlowScreen.showChatUpsale);

  const productRef = React.useRef(product);
  const [answers, setAnswers] = useState(() => getOnboardingAnswersFromURL());
  const role = getParameterFromURL('role');
  const config = getParameterFromURL('config');
  const everydoggyV4 = getParameterFromURL('everydoggyV4');
  const PDF = getParameterFromURL('PDF');
  const currencyISO = getCurrencyValue();
  const testChat = getParameterFromURL('testChat') === 'true';

  useEffect(() => {
    productRef.current = product;
  }, [product]);
  
  useEffect(() => {
    setAnswers(getOnboardingAnswersFromURL());
  }, [role, config]);

  const dispatch = useDispatch();
  const togglePurchaseFlow = (email: string, url: string, payment_system: string, testChat: boolean) => {
    dispatch(updatePurchaseFlow({email: email, url: url, payment_system: payment_system, testChat: testChat}));
  };

  useEffect(() => {
    productAmount = amountValue;
    productName = productNameActive;
    productPlanId = productRef.current?.details?.stripe_plan_id;
    internalId = productRef.current?.details?.internal_product_id;
  }, [productNameActive, productAmountActive, productTrialAmountActive, productRef.current]);
  

  useEffect(() => {
    paymentSystemValue = paymentSystem;
  }, [paymentSystem]);

  useEffect(() => {
    const label = locales[window.userLang].get_your_personalized_plan;
    // `details` is nullable (getWebCatalogProduct resolves null on a failed fetch)
    // and the product may not have arrived yet, so both reads stay optional.
    // `??` — not `||` — because a legitimate '0.00' trial must not fall through to
    // the full amount; `stripe && amount` below already skips a zero total.
    const priceStr = productRef.current?.details?.trial_amount ?? productRef.current?.details?.amount ?? '0';
    const amount = +String(priceStr).replace('.', '');

    if (stripe && amount) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {
            label,
            amount,
          },
        });
      } else {
        const pr = stripe.paymentRequest(paymentRequestOptions(label, amount));

        pr.canMakePayment().then(result => {
          if (result) {
            if(!window.paymentRequestApplePayValue) {
              window.paymentRequestApplePayValue = true;
            }
            setPaymentRequest(pr);
            result.applePay && setPaymentSystem(PAYMENT_SYSTEM.APPLE_PAY);
            result.googlePay && setPaymentSystem(PAYMENT_SYSTEM.GOOGLE_PAY);
            if(result.applePay) {
              window.paymentApplePay = true;
            }
            if(result.googlePay) {
              window.paymentApplePay = false;
            }
            return;
          }
          if(window.paymentRequestApplePayValue) {
            window.paymentRequestApplePayValue = false;
          }
          setPaymentSystem(PAYMENT_SYSTEM.BROWSER_SAVED_CARD);
        });

        pr.on('cancel', async () => {
          analyticsEventsTracker.trackWithTarget(
            GTMOnboardingFunnelEvent.paymentAttemptClosed({
              payment_system: paymentSystemValue,
              product_name: productName,
            }),
          EVENT_TRACKERS.GTM);
        });

        pr.on('paymentmethod', async e => {
          
          viewModel
            .processPaymentAction(e.paymentMethod.id, productPlanId || '', e.payerEmail, internalId, productAmount, productName, paymentSystemValue, answers, showChatUpsale)
            .then((requestPaymentActionResult: PaymentActionResult) => {
              e.complete('success');
              setTimeout(() => {
                const urlProductName = productName.replace(/ /g, "_").replace(/\$/g, "usd");
                const url = `${requestPaymentActionResult.url}&ps=${paymentSystemValue}&pn=${urlProductName}`
                // window.location.replace(url);
                togglePurchaseFlow(
                  e.payerEmail,
                  url,
                  'applePay',
                  testChat ? true : false,
                )
                  
              }, 1000);
            })
            .catch(error => {
              e.complete('fail');
              if(error.message) {
                setAppleError(getLocalizedError(error.message));
              } else {
                setAppleError('error');
              }
              viewModel.processPaymentError(error);
            });
        });
      }
    }
  }, [productRef.current, stripe, paymentRequest, amountValue]);

  return (
    paymentRequest && (
      <div className={s.button}>
        {noPaypal && <h2 className={s.title}>{locales[window.userLang].checkout_title}</h2>}
        <div className={s.buttonWrapper}>
          {everydoggyV4 && (
            <div className={s.customBtn}>
              <p>{locales[window.userLang].payment_text_102}</p>
              <img src={paymentSystem === PAYMENT_SYSTEM.APPLE_PAY ? applePay : gpl} alt='apple Pay' />
            </div>
          )}
          <PaymentRequestButtonElement 
            onClick={() => {
              if (everydoggyV4) {
                analyticsEventsTracker.trackWithTarget(
                  GTMOnboardingFunnelEvent.addToCart(productName, currencyISO, Number(productAmount)),
                  EVENT_TRACKERS.GTM
                );
              }
              let event;
              if (showChatUpsale) {
                event = GTMOnboardingFunnelEvent.paymentIntentionChat(
                  productName,
                  paymentSystem,
                  `${productAmount}`,
                );
              } else {
                event = GTMQuizFunnelEvent.tryPaid({
                  paymentType: paymentSystem,
                  trialPrice: `${productAmount}`,
                  productName: `${productName}`,
                })
              }
              
              analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
            }}
            options={options(paymentRequest)} 
          />
        </div>
        {appleError && <div className={s.error}>{appleError}</div>}
        <ToastContainer
          position="top-center"
          autoClose={false}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        {noPaypal && <Delimiter />}
      </div>
    )
  );
};

export default AppleGooglePayment;
